import { ref, reactive } from 'vue';
import { generalApi } from '@/lib/api/general';
import { formatPhoneNumber } from '@/lib/formatter';

import { Department } from '@/interfaces/model/general/department';
import { User } from '@/interfaces/vuex/user';
import { store } from '@/store/index';
import {
  readAssertedCompanyId,
  readAssertedUserProfile,
} from '@/store/main/getters';
import {
  dispatchGetCurrentUser,
  dispatchCreateCompanyUser,
  dispatchDeleteCompanyUser,
  dispatchGetCompanyUsers,
  dispatchRegenerateActivationMail,
  dispatchUpdateCompanyUser,
  dispatchUpdateCurrentUser,
  dispatchResetCompanyUserPassword,
} from '@/store/main/actionsMain';

export interface CompanyUserFormData {
  email: string;
  firstName: string;
  lastName: string;
  middleName: string;
  departments: Department[];
  position: string;
  phone: string;
  isSubscribe: boolean;
  roleId: number;
}

export const useUserComposable = () => {
  const isLoading = ref<boolean>(false);
  const user = reactive<User>({ ...readAssertedUserProfile(store) });
  const users = ref<User[]>([]);

  const getCompanyUsers = async (options?: {
    fetchAuthInfo?: boolean;
    includeSwitchable?: boolean;
  }): Promise<void> => {
    users.value.splice(0);

    const response = await (options?.includeSwitchable
      ? generalApi.getCompanyUsersWithSwitchable(readAssertedCompanyId(store), {
          fetchAuthInfo: options?.fetchAuthInfo,
        })
      : dispatchGetCompanyUsers(store, {
          companyId: readAssertedCompanyId(store),
          options: { fetchAuthInfo: options?.fetchAuthInfo },
        }));

    if (response) {
      users.value.push(
        ...response.data.map((x) => {
          return {
            uuid: x.uuid,
            first_name: x.first_name,
            last_name: x.last_name,
            middle_name: x.middle_name,
            email: x.email,
            phone: x.phone,
            departments: x.departments,
            position: x.position,
            company_id: x.company_id,
            role_id: x.role_id,
            role: x.role,
            created_at: x.created_at,
            is_active: x.is_active,
            is_subscribe: x.is_subscribe,
            tfa_phone_number: x.tfa_phone_number,
            locale: x.locale,
            two_factor_enabled: x.two_factor_enabled,
            sms_code_enabled: x.sms_code_enabled,
            totp_enabled: x.totp_enabled,
            backup_code_enabled: x.backup_code_enabled,
            login_at: x.login_at,
            is_switchable_user: x.is_switchable_user,
            company_name: x.company_name,
          };
        })
      );
    }
  };

  const regenerateActivationMail = async (data: {
    email: string;
  }): Promise<void> => {
    await dispatchRegenerateActivationMail(store, data);
  };

  const createCompanyUser = async (
    form: CompanyUserFormData
  ): Promise<void> => {
    await dispatchCreateCompanyUser(store, {
      payload: {
        first_name: form.firstName,
        last_name: form.lastName,
        middle_name: form.middleName,
        email: form.email,
        departments: form.departments.map((d) => d.uuid),
        position: form.position,
        phone: formatPhoneNumber(form.phone, 'JP'),
        is_subscribe: form.isSubscribe,
        role_id: form.roleId,
      },
      companyId: readAssertedCompanyId(store),
    });
    await getCompanyUsers();
  };

  const deleteCompanyUser = async (email: string): Promise<void> => {
    await dispatchDeleteCompanyUser(store, {
      companyId: readAssertedCompanyId(store),
      email: email,
    });
    await getCompanyUsers();
  };

  const deactivateCompanyUser = async (userUuid: string): Promise<void> => {
    await generalApi.deactivateCompanyUser(
      readAssertedCompanyId(store),
      userUuid
    );
    await getCompanyUsers();
  };

  const updateCurrentUser = async (): Promise<void> => {
    isLoading.value = true;
    try {
      await dispatchUpdateCurrentUser(store, {
        first_name: user.first_name,
        last_name: user.last_name,
        middle_name: user.middle_name,
        phone: formatPhoneNumber(user.phone, 'JP'),
        position: user.position,
        is_subscribe: user.is_subscribe,
        departments: user.departments
          ? user.departments.map((d) => d.uuid)
          : [],
      });
      await dispatchGetCurrentUser(store);
    } catch (e) {
      console.log(e);
    } finally {
      isLoading.value = false;
    }
  };

  const resetCompanyUserPassword = async (userId: string): Promise<void> => {
    await dispatchResetCompanyUserPassword(store, {
      companyId: readAssertedCompanyId(store),
      userId: userId,
    });
  };

  const updateUser = async (
    userId: string,
    form: Omit<CompanyUserFormData, 'email'>
  ) => {
    isLoading.value = true;
    try {
      await dispatchUpdateCompanyUser(store, {
        companyId: readAssertedCompanyId(store),
        userId: userId,
        firstName: form.firstName,
        lastName: form.lastName,
        middleName: form.middleName,
        position: form.position,
        phone: formatPhoneNumber(form.phone, 'JP'),
        is_subscribe: form.isSubscribe,
        departments: form.departments,
        roleId: form.roleId,
      });
    } catch (e) {
      console.log(e);
    } finally {
      isLoading.value = false;
    }
    if (userId === readAssertedUserProfile(store).uuid) {
      await dispatchGetCurrentUser(store);
    }
  };

  return {
    isLoading,
    user,
    users,
    getCompanyUsers,
    regenerateActivationMail,
    resetCompanyUserPassword,
    createCompanyUser,
    deleteCompanyUser,
    deactivateCompanyUser,
    updateUser,
    updateCurrentUser,
  };
};
